import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePlanoContas } from "@/features/contabil/hooks/usePlanoContas";
import type { ProdutoContabil, SalvarProdutoContabilRequest } from "@/features/produtos/hooks/useProdutoContabil";

interface Props {
  registros: ProdutoContabil[];
  onAdd:     (data: SalvarProdutoContabilRequest) => void;
  onDelete:  (id: number) => void;
  isLoading?: boolean;
}

export function ProdutoContabilTable({ registros, onAdd, onDelete, isLoading }: Props) {
  const { plano } = usePlanoContas();
  const [operacao, setOperacao] = useState("");
  const [debito, setDebito] = useState("");
  const [credito, setCredito] = useState("");
  const [obs, setObs] = useState("");

  const contaLabel = (c: { codigoPlanoConta?: string; descricaoConta?: string }) =>
    `${c.codigoPlanoConta ?? ""} — ${c.descricaoConta ?? ""}`;

  const handleAdd = () => {
    if (!debito && !credito) return;
    onAdd({
      operacaoId:             parseInt(operacao) || 0,
      contaContabilDebitoId:  parseInt(debito) || 0,
      contaContabilCreditoId: parseInt(credito) || 0,
      observacao:             obs || null,
    });
    setOperacao(""); setDebito(""); setCredito(""); setObs("");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border rounded-lg p-3 bg-muted/30">
        <div className="md:col-span-1 space-y-1">
          <Label className="text-xs">Operação</Label>
          <Input type="number" min="0" value={operacao} onChange={(e) => setOperacao(e.target.value)} placeholder="cód." />
        </div>
        <div className="md:col-span-4 space-y-1">
          <Label className="text-xs">Conta Débito</Label>
          <Select value={debito || "__none__"} onValueChange={(v) => setDebito(v === "__none__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">—</SelectItem>
              {plano.map((c) => <SelectItem key={c.id} value={String(c.id)}>{contaLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4 space-y-1">
          <Label className="text-xs">Conta Crédito</Label>
          <Select value={credito || "__none__"} onValueChange={(v) => setCredito(v === "__none__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">—</SelectItem>
              {plano.map((c) => <SelectItem key={c.id} value={String(c.id)}>{contaLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs">Observação</Label>
          <Input value={obs} onChange={(e) => setObs(e.target.value)} />
        </div>
        <div className="md:col-span-1">
          <Button type="button" onClick={handleAdd} disabled={(!debito && !credito) || isLoading} className="w-full" title="Adicionar">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Operação</TableHead>
              <TableHead>Conta Débito</TableHead>
              <TableHead>Conta Crédito</TableHead>
              <TableHead>Observação</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {registros.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">
                  Nenhum registro contábil. Selecione as contas acima e clique em +.
                </TableCell>
              </TableRow>
            )}
            {registros.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-sm">{r.operacaoId || "—"}</TableCell>
                <TableCell className="text-sm">{r.contaContabilDebitoCodigo} — {r.contaContabilDebitoDescricao}</TableCell>
                <TableCell className="text-sm">{r.contaContabilCreditoCodigo} — {r.contaContabilCreditoDescricao}</TableCell>
                <TableCell className="text-sm">{r.observacao}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(r.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
